import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FunctionComponent, useEffect, useState } from 'react';
import { SummaryDetailTable } from './SummaryDetailTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { Period } from '@spike/model';
import moment from 'moment-timezone';
import { useTimeZone } from 'hooks';
import Header from '../../components/Header';
import {
    BookingsSummaryReportState,
    BookingsSummaryReportStatus
} from 'reducers/bookingsSummaryReport/BookingsSummaryReportState';
import { BookingsSummaryReport } from 'model/BookingsReport';
import { getBookingsSummaryReport } from 'actions/insights/bookings/bookingsSummary/BookingsSummaryReportActions';

interface BookingsSummaryProps {
    description?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: 'white',
            height: 'fit-content',

            [theme.breakpoints.up('md')]: {
                //padding: '50px 24px'
            }
        },
        mobileWrapper: {
            [theme.breakpoints.down('sm')]: {
                marginTop: 24
            }
        }
    })
);

export const BookingsSummary: FunctionComponent<BookingsSummaryProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();

    const timeZone = useTimeZone();

    const summaryLoading = useSelector<RootState, boolean>(state => state.bookingsSummaryReport.loading);

    const summaryStatus = useSelector<RootState, BookingsSummaryReportStatus>(
        state => state.bookingsSummaryReport.status
    );

    const summaryReport = useSelector<RootState, BookingsSummaryReportState>(state => state.bookingsSummaryReport);

    const [period, setPeriod] = useState<Period | null>({
        from: moment().tz(timeZone),
        to: moment().tz(timeZone)
    });

    const [summaryReportConverted, setSummaryReportConverted] = useState<BookingsSummaryReport | null>(null);

    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    // export
    const summaryCsvHeaders = [
        'Total Bookings',
        'Total Clients',
        'Total Pets',
        'Total Services',
        'Total Add-Ons',
        'Total Products',
        'Total Custom Items',
        'Total Value',
        'Average Booking Value'
    ];

    const dataToExport = [
        [
            summaryReportConverted?.totalBookings?.toString() || '0',
            summaryReportConverted?.totalClients?.toString() || '0',
            summaryReportConverted?.totalPets?.toString() || '0',
            summaryReportConverted?.totalServices?.toString() || '0',
            summaryReportConverted?.totalAddons?.toString() || '0',
            summaryReportConverted?.totalProducts?.toString() || '0',
            summaryReportConverted?.totalCustomItems?.toString() || '0',
            summaryReportConverted?.totalValue?.toFixed(2) || '0.00',
            summaryReportConverted?.averageBookingValue?.toFixed(2) || '0.00'
        ]
    ];

    const exportFile = {
        name: `Booking Summary - ${marketplace.basics.businessName}.csv`,
        dataInMemory: true,
        dataToExport: dataToExport,
        headers: summaryCsvHeaders
    };

    const calendar = {
        optionSelected: 'Today'
    };

    const [retryAttempt, setRetryAttempt] = useState(0);

    useEffect(() => {
        dispatch(getBookingsSummaryReport(period?.from.format('YYYY/MM/DD'), period?.to.format('YYYY/MM/DD')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => {
        if (summaryStatus === BookingsSummaryReportStatus.GettingSuccess) {
            setSummaryReportConverted(summaryReport.summary);
        }
        if (summaryStatus === BookingsSummaryReportStatus.Error) {
            setPeriod({
                from: moment().tz(timeZone),
                to: moment().tz(timeZone)
            });
            setSummaryReportConverted(null);
            setRetryAttempt(prev => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [summaryStatus]);

    const handleChangePeriod = (dateFilter: Period | null, allTime: boolean) => {
        if (allTime) {
            setPeriod(null);
        } else {
            setPeriod({
                from: dateFilter?.from || moment(),
                to: dateFilter?.to || moment()
            });
        }
    };

    return (
        <Box
            className={classes.container}
            key={retryAttempt}
        >
            <Box>
                <Typography color="textSecondary"> {props.description} </Typography>
            </Box>
            <Box className={classes.mobileWrapper}>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                />
            </Box>
            <SummaryDetailTable
                loading={summaryLoading}
                summary={summaryReportConverted}
            />
        </Box>
    );
};

export default BookingsSummary;
